import { ChangeEvent, FormEvent, useEffect, useState } from 'react'
import { motion } from 'framer-motion'

import ApplicationParagraphs from '../../components/application-paragraphs/ApplicationParagraphs'
import AppLayout from '../../components/app-layout/AppLayout'
import Icon from '../../components/icon/Icon'
import Button from '../../components/button/Button'
import PushPermissionTile from '../../components/push-permission-tile/PushPermissionTile'
import SharePageManager from '../../components/share-page-manager/SharePageManager'
import VerificationsManager from '../../components/verifications-manager/VerificationsManager'
import CompletenessRing from '../../components/completeness-ring/CompletenessRing'
import { evaluateProfileCompleteness } from '../../components/completeness-ring/helpers'
import TextField from '../../components/text-field/TextField'
import TagsInput from '../../components/tags-input/TagsInput'
import NumberField from '../../components/number-field/NumberField'
import ChipPicker from '../../components/chip-picker/ChipPicker'
import ChoiceTileGroup from '../../components/choice-tile/ChoiceTileGroup'
import {
  TilesGrid,
  Tile,
  TileEmoji,
  TileTitle,
  TileDescription,
} from '../../components/choice-tile/styled'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { PropertyType } from '../../_types/property'
import {
  QuickPassDigestFrequency,
  QuickPassDwellingScope,
} from '../../_types/quickpass-profile'
import { DIGEST_FREQUENCY_OPTIONS } from './helpers'
import {
  COMMON_CANADIAN_CITIES,
  BEDROOM_OPTIONS,
  BATHROOM_OPTIONS,
  DWELLING_SCOPE_OPTIONS,
  PROPERTY_TYPE_OPTIONS,
} from '../onboarding/helpers'
import {
  PageStack,
  Header,
  HeaderLeft,
  PageTitle,
  PageSubtitle,
  SectionCard,
  SectionTitle,
  InlineFieldRow,
  FormActions,
  ErrorBanner,
  SuccessBanner,
} from './styled'
import { profileContainerMotion, profileItemMotion } from './helpers'

/**
 * Single-page profile editor. Combines every onboarding step into one
 * scrollable form with a single Save action. Reuses the same field
 * components the wizard does so the UX feels consistent.
 *
 * @returns {JSX.Element}
 */
const ProfilePage = (): JSX.Element => {
  const { profile, patchProfile } = useQuickPassProfile()

  const [firstName, setFirstName] = useState<string>(profile?.firstName ?? '')
  const [lastName, setLastName] = useState<string>(profile?.lastName ?? '')
  const [cities, setCities] = useState<string[]>(
    profile?.unitPreferences.cities ?? [],
  )
  const [priceMin, setPriceMin] = useState<number | null>(
    profile?.unitPreferences.priceMin ?? null,
  )
  const [priceMax, setPriceMax] = useState<number | null>(
    profile?.unitPreferences.priceMax ?? null,
  )
  const [bedroomCounts, setBedroomCounts] = useState<number[]>(
    profile?.unitPreferences.bedroomCounts ?? [],
  )
  const [bathroomCounts, setBathroomCounts] = useState<number[]>(
    profile?.unitPreferences.bathroomCounts ?? [],
  )
  const [dwellingScope, setDwellingScope] = useState<QuickPassDwellingScope>(
    profile?.unitPreferences.dwellingScope ?? 'no-preference',
  )
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>(
    profile?.unitPreferences.propertyTypes ?? [],
  )
  const [monthlyTakeHomeIncome, setMonthlyTakeHomeIncome] = useState<
    number | null
  >(profile?.monthlyTakeHomeIncome ?? null)
  const [digestFrequency, setDigestFrequency] =
    useState<QuickPassDigestFrequency>(profile?.digestFrequency ?? 'weekly')

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [didJustSave, setDidJustSave] = useState<boolean>(false)

  useEffect(() => {
    if (profile === null) {
      return
    }

    setFirstName(profile.firstName)
    setLastName(profile.lastName)
    setCities(profile.unitPreferences.cities)
    setPriceMin(profile.unitPreferences.priceMin)
    setPriceMax(profile.unitPreferences.priceMax)
    setBedroomCounts(profile.unitPreferences.bedroomCounts)
    setBathroomCounts(profile.unitPreferences.bathroomCounts)
    setDwellingScope(profile.unitPreferences.dwellingScope)
    setPropertyTypes(profile.unitPreferences.propertyTypes)
    setMonthlyTakeHomeIncome(profile.monthlyTakeHomeIncome)
    setDigestFrequency(profile.digestFrequency ?? 'weekly')
  }, [profile])

  const handleFirstNameChange = (
    event: ChangeEvent<HTMLInputElement>,
  ): void => {
    setFirstName(event.target.value)
  }

  const handleLastNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setLastName(event.target.value)
  }

  const togglePropertyType = (value: PropertyType): void => {
    if (propertyTypes.includes(value)) {
      setPropertyTypes(propertyTypes.filter(existing => existing !== value))
      return
    }

    setPropertyTypes([...propertyTypes, value])
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    setErrorMessage('')
    setDidJustSave(false)

    if (firstName.trim() === '' || lastName.trim() === '') {
      setErrorMessage('Both first and last name are required.')
      return
    }

    if (cities.length === 0) {
      setErrorMessage('Add at least one city.')
      return
    }

    if (bedroomCounts.length === 0) {
      setErrorMessage('Pick at least one bedroom count.')
      return
    }

    if (priceMin !== null && priceMax !== null && priceMin > priceMax) {
      setErrorMessage('Min price has to be less than max price.')
      return
    }

    if (propertyTypes.length === 0) {
      setErrorMessage('Pick at least one property type.')
      return
    }

    setIsSubmitting(true)

    try {
      await patchProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        monthlyTakeHomeIncome,
        digestFrequency,
        unitPreferences: {
          priceMin,
          priceMax,
          bedroomCounts,
          bathroomCounts,
          dwellingScope,
          cities,
          propertyTypes,
          preferredMoveInDate:
            profile?.unitPreferences.preferredMoveInDate ?? null,
        },
      })
      setDidJustSave(true)
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Save failed. Try again.',
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AppLayout contentMaxWidth="standard">
      <form onSubmit={handleSubmit}>
        <PageStack
          variants={profileContainerMotion}
          initial="hidden"
          animate="visible"
        >
          <Header variants={profileItemMotion}>
            <HeaderLeft>
              <PageTitle>Your profile</PageTitle>
              <PageSubtitle>
                Tune anything that&apos;s shifted. Saved changes flow to every
                supported site.
              </PageSubtitle>
            </HeaderLeft>
            <CompletenessRing
              percent={evaluateProfileCompleteness(profile).percent}
              label="Profile"
            />
          </Header>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Name</SectionTitle>
            <InlineFieldRow>
              <TextField
                label="First name"
                value={firstName}
                onChange={handleFirstNameChange}
                autoComplete="given-name"
              />
              <TextField
                label="Last name"
                value={lastName}
                onChange={handleLastNameChange}
                autoComplete="family-name"
              />
            </InlineFieldRow>
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Where you&apos;re looking</SectionTitle>
            <TagsInput
              label="Cities"
              value={cities}
              onChange={setCities}
              placeholder="Add a city and press Enter"
              suggestions={COMMON_CANADIAN_CITIES}
            />
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Budget &amp; size</SectionTitle>
            <InlineFieldRow>
              <NumberField
                label="Min price / month"
                value={priceMin}
                onChange={setPriceMin}
                prefix="$"
                step={50}
                min={0}
              />
              <NumberField
                label="Max price / month"
                value={priceMax}
                onChange={setPriceMax}
                prefix="$"
                step={50}
                min={0}
              />
            </InlineFieldRow>
            <NumberField
              label="Monthly take-home (optional)"
              value={monthlyTakeHomeIncome}
              onChange={setMonthlyTakeHomeIncome}
              prefix="$"
              step={100}
              min={0}
            />
            <ChipPicker
              label="Bedrooms"
              options={BEDROOM_OPTIONS}
              selectedValues={bedroomCounts}
              onChange={setBedroomCounts}
            />
            <ChipPicker
              label="Bathrooms"
              options={BATHROOM_OPTIONS}
              selectedValues={bathroomCounts}
              onChange={setBathroomCounts}
            />
            <ChoiceTileGroup
              label="Whole place or unit?"
              options={DWELLING_SCOPE_OPTIONS}
              selectedValue={dwellingScope}
              onChange={setDwellingScope}
            />
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Verifications</SectionTitle>
            <VerificationsManager />
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Share your QuickPass</SectionTitle>
            <SharePageManager />
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Notifications</SectionTitle>
            <ChoiceTileGroup
              label="How often should we email you a summary?"
              options={DIGEST_FREQUENCY_OPTIONS}
              selectedValue={digestFrequency}
              onChange={setDigestFrequency}
              helperText="In-app notifications stay on regardless — this only affects email."
            />
            <PushPermissionTile />
          </SectionCard>

          <SectionCard variants={profileItemMotion}>
            <SectionTitle>Property types</SectionTitle>
            <TilesGrid>
              {PROPERTY_TYPE_OPTIONS.map(option => {
                const isSelected = propertyTypes.includes(option.value)
                return (
                  <Tile
                    key={option.value}
                    type="button"
                    isSelected={isSelected}
                    onClick={() => togglePropertyType(option.value)}
                  >
                    <TileEmoji aria-hidden="true">
                      <Icon icon={option.icon} />
                    </TileEmoji>
                    <TileTitle>{option.title}</TileTitle>
                    <TileDescription>{option.description}</TileDescription>
                  </Tile>
                )
              })}
            </TilesGrid>
          </SectionCard>

          {errorMessage !== '' ? (
            <ErrorBanner variants={profileItemMotion}>
              {errorMessage}
            </ErrorBanner>
          ) : null}

          {didJustSave === true ? (
            <SuccessBanner variants={profileItemMotion}>
              Saved! Your QuickPass is up to date.
            </SuccessBanner>
          ) : null}

          <FormActions variants={profileItemMotion}>
            <Button type="submit" isLoading={isSubmitting}>
              Save changes
            </Button>
          </FormActions>

          <motion.div variants={profileItemMotion}>
            <ApplicationParagraphs />
          </motion.div>
        </PageStack>
      </form>
    </AppLayout>
  )
}

export default ProfilePage
